import type { DsTokens, ThemeColors } from '@/types/themeTypes/ThemeType';

/**
 * Tokens del rediseño (prefijo `ds`).
 *
 * Son ADITIVOS: no reemplazan ni modifican ningún color existente del tema.
 * El módulo `mi-tablero` y el sidebar nuevo los consumen; el resto de la app
 * sigue usando la paleta de siempre (`textPrimary`, `background`, etc.).
 *
 * Mapeo token-por-token y racional: `rediseno-tablero/TOKENS.md`.
 *
 * ── Por qué se derivan en vez de venir en duro ──────────────────────────────
 * Este repo es un CORE: la misma base la usan varias marcas (ver
 * `rediseno-tablero/CORE.md`). Los valores del prototipo son de PyLC —neutros
 * teñidos de morado y un teal propio—, así que ponerlos como respaldo del core
 * le impondría la identidad de PyLC a cualquier cliente nuevo.
 *
 * En su lugar, el respaldo se **deriva de la paleta que la marca ya declara**:
 * una marca con primario verde obtiene una escala coherente con su verde, no
 * con el morado de PyLC. Y cualquier marca puede fijar el valor exacto que
 * quiera declarándolo en su `colorsConfig` (PyLC lo hace, con los valores
 * literales del prototipo).
 */

/** Último recurso: si una marca no declara ni siquiera su paleta base. */
const NEUTRO_CLARO = {
    ink: '#212529',
    text: '#495057',
    textMuted: '#6c757d',
    textSubtle: '#adb5bd',
    textFaint: '#ced4da',
    lineStrong: '#ced4da',
    line: '#dee2e6',
    border: '#e5eaef',
    divider: '#f1f3f5',
    bg: '#f8f9fa',
    tint: '#f1f3f5',
    hover: '#f8f9fa',
    accent: '#28a745',
    accentBg: '#e6f9ef',
    danger: '#dc3545',
    dangerBg: '#fde2e2',
    dangerOnDark: '#ff9db4',
    warn: '#a9670a',
    warnBg: '#fff7e6',
    // Cimiento del portal del agente: superficies muy tenues, línea morada y
    // acento morado brillante. Últimos recursos, si la marca no declara paleta.
    surfaceSubtle: '#fbfafd',
    surfaceHover: '#f9f8fc',
    linePurple: '#e0d8ee',
    linePurpleSoft: '#e7e1f3',
    warnBorder: '#ffe2b0',
    dangerBgSoft: '#fff1f4',
    // Task 1.28 (#8554): banner ámbar tenue, cuadre OK teal y barra del visor.
    warnBgSoft: '#fff7e6',
    okBgSoft: '#f2fbf9',
    okBorder: '#cbede7',
    viewerBar: '#1e3a8a',
    // Versión final: fondo del botón secundario. Tinte muy tenue del morado.
    purple08: 'rgba(81, 26, 107, 0.08)',
    // Control deshabilitado (§7.1): gris azulado de fondo y gris de texto.
    disabledBg: '#e7ecf0',
    disabledText: '#9aa4ad',
    // Superficie de tarjeta ds: blanco en claro.
    surface: '#ffffff',
    // Estados §8: fondo del bloque de estado, donde difiere de la píldora.
    warnBlockBg: '#fff9ec',
    tealBlockBg: '#eaf8f6',
    tealBlockBorder: '#b8e8e1',
    dangerBlockBorder: '#fbd5de'
};

const NEUTRO_OSCURO = {
    ink: '#eaeff4',
    text: '#d6dbe0',
    textMuted: '#a9b0b8',
    textSubtle: '#8b9199',
    textFaint: '#6e747d',
    lineStrong: '#3d434f',
    line: '#333944',
    border: '#2b303a',
    divider: '#242830',
    bg: '#1a1d24',
    tint: '#242830',
    hover: '#222630',
    accent: '#2ad98a',
    accentBg: '#0d3b24',
    danger: '#ff7a99',
    dangerBg: '#43202b',
    dangerOnDark: '#ff9db4',
    warn: '#ffc061',
    warnBg: '#3d2f16',
    // Derivados para oscuro conservando matiz e invirtiendo luminosidad; sin
    // validación de diseño (el prototipo no tiene modo oscuro).
    surfaceSubtle: '#20242c',
    surfaceHover: '#222630',
    linePurple: '#333944',
    linePurpleSoft: '#3a3350',
    warnBorder: '#5c4a1f',
    dangerBgSoft: '#3a2029',
    // Task 1.28 (#8554), variante oscura derivada.
    warnBgSoft: '#3d2f16',
    okBgSoft: '#142421',
    okBorder: '#1f5049',
    viewerBar: '#1e3a8a',
    // Versión final, variante oscura derivada (el prototipo no tiene oscuro).
    purple08: 'rgba(143, 123, 209, 0.14)',
    // Control deshabilitado, variante oscura derivada.
    disabledBg: '#2b303a',
    disabledText: '#6e747d',
    // Superficie de tarjeta ds en oscuro: near-black del invert del rediseño.
    surface: '#141414',
    // Estados §8, variante oscura derivada (el prototipo no tiene oscuro).
    warnBlockBg: '#2a2010',
    tealBlockBg: '#182b28',
    tealBlockBorder: '#1f3d38',
    dangerBlockBorder: '#4a2530'
};

/**
 * Construye el respaldo de los tokens `ds` a partir de la paleta de la marca.
 * Cada token cae a su equivalente más cercano del tema y, si la marca tampoco
 * lo declara, a un neutro final.
 */
export function buildDsFallback(colors: ThemeColors, modo: 'light' | 'dark'): Required<DsTokens> {
    const n = modo === 'dark' ? NEUTRO_OSCURO : NEUTRO_CLARO;

    return {
        // Escala de texto y líneas: se toma la del tema de la marca.
        dsInk: colors.textPrimary ?? n.ink,
        dsText: colors.textSecondary ?? n.text,
        dsTextMuted: colors.grey200 ?? n.textMuted,
        dsTextSubtle: colors.grey100 ?? n.textSubtle,
        dsTextFaint: n.textFaint,
        dsLineStrong: colors.borderColor ?? n.lineStrong,
        dsLine: colors.inputBorder ?? n.line,
        dsBorder: n.border,
        dsDivider: colors.bglight ?? n.divider,
        dsBg: colors.background ?? n.bg,

        // Filas destacadas: tinte del primario de la marca.
        dsRowUnread: colors.lightprimary ?? n.tint,
        dsRowHover: colors.hoverColor ?? n.hover,

        // Morado de marca. El 600 del prototipo es el `primary` del tema, así
        // que no se duplica: usar `primary` directamente.
        dsPurple700: colors.primary ?? n.ink,
        dsPurple50: colors.lightprimary ?? n.tint,

        // Acento de datos. En PyLC es el teal One360; para otra marca, su
        // color de éxito, que cumple el mismo papel semántico.
        dsTeal400: colors.success ?? n.accent,
        dsTeal600: colors.success ?? n.accent,
        dsTeal700: colors.success ?? n.accent,
        dsTeal800: colors.success ?? n.accent,
        dsTeal50: colors.lightsuccess ?? n.accentBg,

        // Estados.
        dsDanger: colors.error ?? n.danger,
        dsDangerDot: colors.errorSecondary ?? colors.error ?? n.danger,
        dsDangerBg: colors.lighterror ?? n.dangerBg,
        // Rosa que sigue siendo legible sobre una imagen oscura.
        dsDangerOnDark: n.dangerOnDark,
        dsWarn: n.warn,
        dsWarnDot: colors.warning ?? n.warn,
        dsWarnBg: colors.lightwarning ?? n.warnBg,

        // ── Cimiento del portal del agente ──────────────────────────────────
        // Superficies aún más tenues que dsBg y su hover: se derivan del fondo
        // de la marca; su ausencia cae a un neutro casi blanco.
        dsSurfaceSubtle: colors.background ?? n.surfaceSubtle,
        dsSurfaceHover: colors.hoverColor ?? n.surfaceHover,
        // Línea morada sobre fondo teñido: comparte rol con la línea del tema.
        dsLinePurple: colors.borderColor ?? n.linePurple,
        dsLinePurpleSoft: n.linePurpleSoft,
        // Chip y acento morado: derivan del primario de la marca, no del morado
        // de PyLC, para no imponer su identidad.
        dsPurple100: colors.lightprimary ?? n.tint,
        dsPurple500: colors.primary ?? n.ink,
        // Borde de aviso ámbar y fondo de alerta claro: del ámbar/rojo del tema.
        dsWarnBorder: colors.warning ?? n.warnBorder,
        dsDangerBgSoft: colors.lighterror ?? n.dangerBgSoft,

        // ── Task 1.28 (#8554): tokens de Pólizas pixel-exact ────────────────
        // Banner ámbar tenue "falta dato": del ámbar del tema (NO es dsWarnBg,
        // que es la píldora/fila #fff4e0). Cuadre OK teal: del éxito del tema.
        // Barra del visor PDF: azul funcional, sin equivalente de marca → neutro.
        dsWarnBgSoft: colors.lightwarning ?? n.warnBgSoft,
        dsOkBgSoft: colors.lightsuccess ?? n.okBgSoft,
        dsOkBorder: colors.success ?? n.okBorder,
        dsViewerBar: n.viewerBar,

        // ── Versión final (backlog #8289, task 1.1) ─────────────────────────
        // Fondo del botón secundario: tinte translúcido del primario. Se deriva
        // del `lightprimary` de la marca (tinte de su propio primario), no del
        // morado de PyLC, para no imponer su identidad a otra marca.
        dsPurple08: colors.lightprimary ?? n.purple08,

        // Control deshabilitado (§7.1). Se derivan de los neutros del tema: el
        // fondo del borde de la marca y el texto de su gris tenue. Su ausencia
        // cae al gris azulado y gris del prototipo.
        dsDisabledBg: colors.borderColor ?? n.disabledBg,
        dsDisabledText: colors.grey100 ?? n.disabledText,

        // Superficie de tarjeta ds: el `surface` de la marca. En claro blanco,
        // en oscuro el near-black del rediseño (o el surface oscuro de la marca).
        dsSurface: colors.surface ?? n.surface,

        // ── Estados §8 (extensión de D-colores-globales a botones/estados) ──
        // Fondo del BLOQUE de estado, distinto del fondo de la píldora en 3 de
        // los 5 estados. Sin equivalente de marca genérico: cae al neutro.
        dsWarnBlockBg: n.warnBlockBg,
        dsTealBlockBg: n.tealBlockBg,
        dsTealBlockBorder: n.tealBlockBorder,
        dsDangerBlockBorder: n.dangerBlockBorder
    };
}

/**
 * Mezcla el respaldo con lo que declare la marca. Solo se toma del override lo
 * que venga definido, para que un `undefined` no borre el valor derivado.
 */
export function resolveDsTokens(colors: ThemeColors, modo: 'light' | 'dark'): Required<DsTokens> {
    const resultado = buildDsFallback(colors, modo);

    for (const clave of Object.keys(resultado) as (keyof DsTokens)[]) {
        const valor = colors[clave];
        if (valor) resultado[clave] = valor;
    }

    return resultado;
}
